import React from "react";
import Dialog from "@mui/material/Dialog";
import { Box, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Cancel, CheckCircle, Close, Error, Info } from "@mui/icons-material";
import { tokens } from "../../theme";
import Button from "./Button";
import { useModalStyles } from "./useModalStyles";

interface AlertDialogProps {
  open: boolean;
  content: string;
  onClose: () => void;
  title: string;
  confirmButton?: string;
  onConfirm?: () => void;
  severity: "error" | "success" | "warning" | "info";
  /** Raise above a parent dialog that sits higher than MUI's default modal layer. */
  zIndex?: number;
}

const InfoPopup: React.FC<AlertDialogProps> = ({
  open,
  content,
  onClose,
  title,
  severity,
  confirmButton,
  onConfirm,
  zIndex,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const colors = tokens(theme.palette.mode);
  const {
    tokens: modalTokens,
    paperSx,
    headerSx,
    closeIconSx,
    bodySx,
    footerSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();

  const renderSeverityIcon = () => {
    switch (severity) {
      case "error":
        return (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              mr: 3,
              padding: 3,
              backgroundColor: "rgba(145, 42, 42, 0.3)",
            }}
          >
            <Cancel sx={{ color: colors.error[400], fontSize: "36px" }} />
          </Box>
        );
      case "success":
        return (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              mr: 3,
              padding: 3,
              backgroundColor: "rgba(23, 126, 73, 0.3)",
            }}
          >
            <CheckCircle
              sx={{ color: colors.success[400], fontSize: "36px" }}
            />
          </Box>
        );
      case "warning":
        return (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              mr: 3,
              padding: 3,
              backgroundColor: "rgba(100, 91, 9, 0.3)",
            }}
          >
            <Error sx={{ color: colors.warning[400], fontSize: "36px" }} />
          </Box>
        );
      case "info":
      default:
        return (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              mr: 3,
              padding: 3,
              backgroundColor: "rgba(66, 65, 116, 0.3)",
            }}
          >
            <Info sx={{ color: colors.info[400], fontSize: "36px" }} />
          </Box>
        );
    }
  };

  return (
    <Dialog
      open={open}
      keepMounted
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      sx={zIndex !== undefined ? { zIndex } : undefined}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <Box sx={headerSx}>
        <Box sx={{ display: "flex", alignItems: "center", width: "100%", flex: 1, pr: 4 }}>
          {renderSeverityIcon()}
          <Box gap={2} sx={{ flex: 1 }}>
            <Typography id="alert-dialog-title" sx={{ fontWeight: "bold", mb: 0, fontSize: "1.05rem", color: modalTokens.textPrimary }}>
              {title}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
          <Close sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>
      <Box sx={bodySx}>
        <Typography
          id="alert-dialog-description"
          variant="body2"
          sx={{ color: modalTokens.textSecondary, fontSize: "0.9rem" }}
        >
          {content}
        </Typography>
      </Box>
      <Box sx={footerSx}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            gap: 1.2,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          {
            confirmButton &&
            <Box
              component="button"
              type="button"
              onClick={onConfirm}
              sx={{
                ...primaryBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                px: 3,
                py: 1.1,
                border: "none",
                cursor: "pointer",
                color: "#fff",
                order: { xs: 1, sm: 2 },
              }}
            >
              {confirmButton}
            </Box>
          }
          <Box
            component="button"
            type="button"
            onClick={onClose}
            sx={{
              ...cancelBtnSx,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              px: 3,
              py: 1.1,
              border: "1px solid",
              cursor: "pointer",
              order: { xs: 2, sm: 1 },
            }}
          >
            Close
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default InfoPopup;
